import { useState, useEffect, useCallback } from 'react';
import { getFirestore, doc, getDoc, updateDoc, increment, collection, query, where, getDocs } from 'firebase/firestore';

export const useReportes = (usuarioActual, idioma) => {
  const db = getFirestore();
  const [limites, setLimites] = useState(null);
  const [cargandoLimites, setCargandoLimites] = useState(true);
  
  const plan = usuarioActual?.plan || 'starter';
  const lang = idioma === 'en' ? 'en' : 'es';
  
  // ============================================================
  // CONFIGURACIÓN DE REPORTES POR PLAN (CORREGIDA)
  // ============================================================
  const REPORTES_CONFIG = {
    starter: {
      // Starter: VE todos los reportes (como Elite) pero SOLO 1 descarga TOTAL
      permitidos: 'todos',
      limiteMensual: 1,
      maxSeleccion: 999, // Puede seleccionar todos, pero solo 1 descarga
      esConsolidado: true,
      mensaje: lang === 'en' 
        ? '✅ You have used your only consolidated report. Upgrade to ELITE for unlimited downloads.'
        : '✅ Has usado tu único reporte consolidado. Pásate a ELITE para descargas ilimitadas.'
    },
    pro: {
      // Pro: SOLO 5 reportes (Ventas, Compras, Inventario, PyG, Gastos)
      permitidos: ['ventas', 'compras', 'inventario', 'pyg', 'gastos'],
      limiteMensual: 5,
      maxSeleccion: 1,
      esConsolidado: false,
      mensaje: lang === 'en'
        ? '📊 You have reached the limit of 5 reports this month. Upgrade to Business for more.'
        : '📊 Has alcanzado el límite de 5 reportes este mes. Actualiza a Business para más.'
    },
    business: {
      // Business: 10 reportes (Pro + 5 adicionales)
      permitidos: [
        'ventas', 'compras', 'inventario', 'pyg', 'gastos',
        'flujoCaja', 'cuentasPagar', 'cuentasCobrar', 'kpi', 'roi'
      ],
      limiteMensual: 20,
      maxSeleccion: 10,
      esConsolidado: false,
      mensaje: lang === 'en'
        ? '📊 You have reached the limit of 20 reports this month. Upgrade to Elite for unlimited.'
        : '📊 Has alcanzado el límite de 20 reportes este mes. Actualiza a Elite para ilimitados.'
    },
    elite: {
      // Elite: TODOS los reportes, hasta 100 por mes
      permitidos: 'todos',
      limiteMensual: 100,
      maxSeleccion: 999,
      esConsolidado: false,
      mensaje: null
    }
  };
  
  const config = REPORTES_CONFIG[plan];
  
  // ============================================================
  // VERIFICAR SI PUEDE GENERAR REPORTE
  // ============================================================
  const puedeGenerarReporte = async (tipoReporte, cantidad = 1) => {
    if (!usuarioActual?.uid) return false;
    
    // Elite: ilimitado (hasta 100)
    if (plan === 'elite') return true;
    
    // Verificar si el tipo de reporte está permitido para este plan
    if (config.permitidos !== 'todos' && !config.permitidos.includes(tipoReporte)) {
      return false;
    }
    
    try {
      const userRef = doc(db, 'usuarios', usuarioActual.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      
      const hoy = new Date();
      const mesActual = `${hoy.getFullYear()}-${hoy.getMonth() + 1}`;
      const contador = userData?.reportesGenerados?.[mesActual] || 0;
      
      // Starter: verificar si ya usó su único reporte
      if (plan === 'starter') {
        const hasGeneratedConsolidated = userData?.hasGeneratedConsolidated || false;
        if (hasGeneratedConsolidated) return false;
        return true;
      }
      
      // Pro y Business: verificar límite mensual
      if (contador >= config.limiteMensual) {
        return false;
      }
      
      return true;
      
    } catch (error) {
      console.error('Error verificando límite:', error);
      return false;
    }
  };
  
  // ============================================================
  // REGISTRAR GENERACIÓN DE REPORTE
  // ============================================================
  const registrarReporteGenerado = async (cantidad = 1) => {
    if (!usuarioActual?.uid) return;
    if (plan === 'elite') return;
    
    try {
      const userRef = doc(db, 'usuarios', usuarioActual.uid);
      const hoy = new Date();
      const mesActual = `${hoy.getFullYear()}-${hoy.getMonth() + 1}`;
      
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const contadorActual = userData?.reportesGenerados?.[mesActual] || 0;
      
      await updateDoc(userRef, {
        [`reportesGenerados.${mesActual}`]: contadorActual + cantidad,
        ultimoReporte: new Date().toISOString()
      });
      
      // Si es Starter, marcar que ya usó su reporte consolidado
      if (plan === 'starter') {
        await updateDoc(userRef, {
          hasGeneratedConsolidated: true
        });
      }
      
    } catch (error) {
      console.error('Error registrando reporte:', error);
    }
  };
  
  // ============================================================
  // OBTENER MENSAJE DE UPGRADE
  // ============================================================
  const getMensajeUpgrade = (tipoReporte) => {
    if (plan === 'elite') return null;
    
    // Starter: mensaje especial
    if (plan === 'starter') {
      return lang === 'en' 
        ? '✅ You have used your only consolidated report. Upgrade to ELITE for unlimited downloads.'
        : '✅ Has usado tu único reporte consolidado. Pásate a ELITE para descargas ilimitadas.';
    }
    
    // Si el tipo no está permitido
    if (config.permitidos !== 'todos' && !config.permitidos.includes(tipoReporte)) {
      if (lang === 'en') {
        return `🔒 This report is available in Business and Elite plans. Upgrade to access.`;
      }
      return `🔒 Este reporte está disponible en planes Business y Elite. Actualiza para acceder.`;
    }
    
    // Si excedió el límite
    return config.mensaje;
  };
  
  // ============================================================
  // OBTENER REPORTES DISPONIBLES PARA EL MODAL
  // ============================================================
  const getReportesDisponibles = () => {
    if (config.permitidos === 'todos') return 'todos';
    return config.permitidos;
  };
  
  // ============================================================
  // OBTENER LÍMITE MÁXIMO DE SELECCIÓN PARA EL MODAL
  // ============================================================
  const getMaxSeleccion = () => {
    return config.maxSeleccion || 1;
  };
  
  // ============================================================
  // VERIFICAR SI PUEDE SELECCIONAR MÚLTIPLES REPORTES
  // ============================================================
  const puedeSeleccionarMultiples = () => {
    // Pro: solo 1 a la vez
    if (plan === 'pro') return false;
    // Business: puede seleccionar hasta 10
    if (plan === 'business') return true;
    // Starter: puede seleccionar todos (pero solo 1 descarga)
    if (plan === 'starter') return true;
    // Elite: puede seleccionar todos
    return true;
  };
  
  // ============================================================
  // VERIFICAR SI EL PLAN REQUIERE REPORTE CONSOLIDADO (STARTER)
  // ============================================================
  const esConsolidado = () => {
    return config.esConsolidado || false;
  };
  
  // ============================================================
  // OBTENER LÍMITE MENSUAL
  // ============================================================
  const getLimiteMensual = () => {
    return config.limiteMensual;
  };
  
  return {
    puedeGenerarReporte,
    registrarReporteGenerado,
    getMensajeUpgrade,
    getReportesDisponibles,
    getMaxSeleccion,
    puedeSeleccionarMultiples,
    esConsolidado,
    getLimiteMensual,
    plan,
    cargandoLimites,
    config
  };
};

